import { useState, useEffect } from 'react';
import { shiftsAPI } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Trash2, Clock, Coffee, Plus } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const SHIFT_TYPES = [
  { value: 'normal', label: 'Normal' },
  { value: 'ecole', label: 'École' },
  { value: 'conge', label: 'Congé' },
  { value: 'maladie', label: 'Maladie' },
];

export default function ShiftEditDialog({ open, onClose, employeeId, date, shift, onSave, onDelete }) {
  const [shiftType, setShiftType] = useState('normal');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [breakMinutes, setBreakMinutes] = useState(60);
  const [hasTimeRange, setHasTimeRange] = useState(false);
  const [loading, setLoading] = useState(false);

  const isEdit = !!shift;
  const isSpecial = shiftType !== 'normal';

  useEffect(() => {
    if (shift) {
      setShiftType(shift.shift_type);
      setStartTime(shift.start_time);
      setEndTime(shift.end_time);
      setBreakMinutes(shift.break_minutes);
      setHasTimeRange(shift.shift_type !== 'normal' && shift.start_time !== '00:00');
    } else {
      setShiftType('normal');
      setStartTime('09:00');
      setEndTime('17:00');
      setBreakMinutes(60);
      setHasTimeRange(false);
    }
  }, [shift, open]);

  const handleSave = async () => {
    const st = isSpecial && !hasTimeRange ? '00:00' : startTime;
    const et = isSpecial && !hasTimeRange ? '00:00' : endTime;
    const brk = isSpecial ? 0 : breakMinutes;

    setLoading(true);
    try {
      if (isEdit) {
        await shiftsAPI.update(shift.id, {
          start_time: st,
          end_time: et,
          break_minutes: brk,
          shift_type: shiftType,
        });
        toast.success('Shift mis à jour');
      } else {
        await shiftsAPI.create({
          employee_id: employeeId,
          date,
          start_time: st,
          end_time: et,
          break_minutes: brk,
          shift_type: shiftType,
        });
        toast.success('Shift créé');
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!shift) return;
    setLoading(true);
    try {
      await onDelete(shift.id);
      onClose();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
    setLoading(false);
  };

  const dateLabel = date ? format(parseISO(date), 'EEEE d MMMM yyyy', { locale: fr }) : '';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="shift-edit-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Modifier le shift' : 'Ajouter un shift'}</DialogTitle>
          <DialogDescription className="capitalize">{dateLabel}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label>Type de shift</Label>
            <Select value={shiftType} onValueChange={(v) => {
              setShiftType(v);
              if (v !== 'normal') {
                setBreakMinutes(0);
              } else {
                setBreakMinutes(60);
              }
            }}>
              <SelectTrigger data-testid="shift-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHIFT_TYPES.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Special shift: optional time range toggle */}
          {isSpecial && (
            <div className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
              <Label htmlFor="has-time-range" className="text-sm text-slate-600 cursor-pointer">
                Préciser une plage horaire
              </Label>
              <Switch
                data-testid="shift-time-range-toggle"
                id="has-time-range"
                checked={hasTimeRange}
                onCheckedChange={setHasTimeRange}
              />
            </div>
          )}

          {/* Time inputs: always for normal, optional for special */}
          {(!isSpecial || hasTimeRange) && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Début
                </Label>
                <Input
                  data-testid="shift-start-time"
                  type="time"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  className="font-data"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Fin
                </Label>
                <Input
                  data-testid="shift-end-time"
                  type="time"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  className="font-data"
                />
              </div>
            </div>
          )}

          {/* Break: only for normal shifts */}
          {!isSpecial && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Coffee className="w-3.5 h-3.5" strokeWidth={1.5} />
                Pause (minutes)
              </Label>
              <Input
                data-testid="shift-break-minutes"
                type="number"
                min={0}
                max={180}
                value={breakMinutes}
                onChange={e => setBreakMinutes(parseInt(e.target.value) || 0)}
                className="font-data"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {isEdit && (
            <Button
              data-testid="shift-delete-btn"
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={loading}
              className="mr-auto"
            >
              <Trash2 className="w-4 h-4 mr-1" strokeWidth={1.5} />
              Supprimer
            </Button>
          )}
          <Button data-testid="shift-cancel-btn" variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button data-testid="shift-save-btn" onClick={handleSave} disabled={loading}>
            {loading ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Ajouter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
